/**
 * Marketing attribution capture.
 *
 * Click ids arrive on the landing URL and are gone the moment the customer
 * navigates, so they are captured once on first load and persisted for the
 * session. Without this, a lead from a paid click reaches the CRM looking
 * organic and the campaign gets no credit for it.
 */

const STORAGE_KEY = 'sob.attribution';
const SESSION_KEY = 'sob.session';

const TRACKED_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'fbclid',
  'ttclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'campaign_id',
  'adgroup_id',
  'keyword',
  'matchtype',
  'device',
  'placement',
];

/* Storage can throw outright in private modes and embedded webviews, so every
   access is guarded — losing attribution is acceptable, crashing the journey
   is not. */
function safeGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function readFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const found = {};
  for (const key of TRACKED_PARAMS) {
    const value = params.get(key);
    if (value) found[key] = value.slice(0, 512);
  }
  return found;
}

/**
 * Attribution for this visit: whatever is on the URL now, merged over anything
 * captured earlier in the session. URL params win, so a customer who clicks a
 * second, different ad is credited to the ad that actually converted them.
 */
export function getAttribution() {
  const stored = (() => {
    try {
      return JSON.parse(safeGet(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  })();

  const fromUrl = readFromUrl();

  const attribution = {
    ...stored,
    ...fromUrl,
    landing_url: stored.landing_url || window.location.href.slice(0, 2000),
    referrer: stored.referrer || (document.referrer ? document.referrer.slice(0, 2000) : undefined),
  };

  for (const key of Object.keys(attribution)) {
    if (attribution[key] === undefined) delete attribution[key];
  }

  safeSet(STORAGE_KEY, JSON.stringify(attribution));
  return attribution;
}

/**
 * A v4 UUID without depending on `crypto.randomUUID`, which only exists in a
 * secure context — not in every in-app webview, and those are a large share of
 * paid traffic.
 */
function uuidv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last resort. Not cryptographically random, but this id only stitches a
  // funnel together — a collision costs one muddled analytics row.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** A stable id for this visit, used to stitch funnel steps to the final lead. */
export function getSessionUid() {
  let uid = safeGet(SESSION_KEY);
  if (!uid) {
    uid = uuidv4();
    safeSet(SESSION_KEY, uid);
  }
  return uid;
}
